import { useEffect, useState } from "react";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import { toast } from "react-toastify";
import { whatsAppService } from "../../../Api/Services/whatsAppService";
import { useAuth } from "../../../Contexts/AuthContext";
import { WhatsAppProvider } from "../../../Enums/WhatsAppProvider";
import { WhatsAppStatus, WhatsAppTemplate } from "../../../Models/WhatsApp";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import BackofficePage from "../../../Components/BackofficePage/BackofficePage";
import WhatsAppTemplateCard from "../../../Components/WhatsAppTemplateCard/WhatsAppTemplateCard";
import WhatsAppMessageHistory from "../../../Components/WhatsAppMessageHistory/WhatsAppMessageHistory";
import Button from "../../../Components/Button/Button";

const PROVIDER_LABELS: Record<WhatsAppProvider, string> = {
  [WhatsAppProvider.ShortNotify]: "ShortNotify",
  [WhatsAppProvider.Meta]: "Meta (WhatsApp Business)"
};

const AdminWhatsAppPage = () => {
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isSyncingMeta, setIsSyncingMeta] = useState(false);

  const usesMeta = status?.provider === WhatsAppProvider.Meta;

  useEffect(() => {
    whatsAppService.getStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  // Templates are admin-only; operators only see the history
  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    setIsLoadingTemplates(true);
    whatsAppService.getTemplates()
      .then(setTemplates)
      .catch(() => toast.error("Não foi possível carregar as mensagens."))
      .finally(() => setIsLoadingTemplates(false));
  }, [isAdmin]);

  const handleTemplateSaved = (saved: WhatsAppTemplate) => {
    setTemplates((current) => current.map((t) => (t.type === saved.type ? saved : t)));
  };

  const handleSyncMeta = async () => {
    try {
      setIsSyncingMeta(true);
      setTemplates(await whatsAppService.syncMetaTemplates());
      toast.success("Mensagens enviadas para aprovação da Meta.");
    } catch {
      toast.error("Não foi possível enviar as mensagens para a Meta.");
    } finally {
      setIsSyncingMeta(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SmartNavbar />
      <BackofficePage title={"WHATSAPP"} className="max-w-full" fullHeight>
        {status && (
          <div
            className={`mb-6 flex items-start gap-3 border rounded p-4 text-sm ${
              status.isConfigured ? "bg-green-50 border-green-200 text-green-900" : "bg-yellow-50 border-yellow-200 text-yellow-900"
            }`}
          >
            {status.isConfigured ? <FaCheckCircle className="mt-0.5 shrink-0" /> : <FaExclamationTriangle className="mt-0.5 shrink-0" />}
            <p>
              {status.isConfigured
                ? `As mensagens automáticas são enviadas aos clientes pelo WhatsApp, via ${PROVIDER_LABELS[status.provider]}.`
                : "O WhatsApp da loja ainda não foi configurado, então as mensagens automáticas não estão sendo enviadas. Fale com o suporte da ESSA para ativar."}
            </p>
          </div>
        )}

        {isAdmin ? (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(22rem,2fr)] gap-6 items-start">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h2 className="font-semibold">{"Mensagens automáticas"}</h2>
                  <p className="text-sm text-gray-600">
                    {"Edite o texto de cada mensagem. Os campos entre colchetes, como [Nome], são preenchidos automaticamente."}
                  </p>
                </div>
                {usesMeta && (
                  <Button isLoading={isSyncingMeta}
                    type="button"
                    onClick={() => void handleSyncMeta()}
                    disabled={isSyncingMeta}
                    className="shrink-0 text-sm px-4 py-2"
                  >
                    {"Enviar para aprovação da Meta"}
                  </Button>
                )}
              </div>
              {usesMeta && (
                <p className="text-xs text-gray-500">
                  {"Na Meta, cada texto precisa ser aprovado antes de chegar a quem não conversou com a loja nas últimas 24 horas."}
                </p>
              )}

              {isLoadingTemplates
                ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="animate-pulse bg-white border rounded p-4 shadow-sm h-48" />
                ))
                : templates.map((template) => (
                  <WhatsAppTemplateCard
                    key={template.type}
                    template={template}
                    showMetaStatus={usesMeta}
                    onSaved={handleTemplateSaved}
                  />
                ))}
            </div>

            <WhatsAppMessageHistory />
          </div>
        ) : (
          <WhatsAppMessageHistory />
        )}
      </BackofficePage>
    </div>
  );
};

export default AdminWhatsAppPage;
